import React from 'react';
import { ArrowRight } from 'lucide-react';
import type { ComponentDiff, DiffKind, PlanDiff } from '../../utils/planDiff';
import './PlanDiffView.css';

interface PlanDiffViewProps {
    diff: PlanDiff;
    yearsToShow: number;
    onYearsToShowChange: (years: number) => void;
    /** Clicking a component selects it in the draft; without it the rows are not clickable */
    onSelectComponent?: (componentKey: string) => void;
    selectedComponentKey?: string | null;
}

const KIND_LABELS: Record<DiffKind, string> = {
    added: 'Ny i planen',
    removed: 'Tas bort',
    changed: 'Ändrad'
};

const formatKr = (amount: number): string => `${Math.round(amount).toLocaleString('sv-SE')} kr`;

const formatDelta = (amount: number): string => {
    const rounded = Math.round(amount);
    return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('sv-SE')} kr`;
};

const DeltaBadge: React.FC<{ amount: number }> = ({ amount }) =>
    Math.round(amount) === 0
        ? null
        : <span className={`plan-diff-delta ${amount > 0 ? 'cost-diff-positive' : 'cost-diff-negative'}`}>{formatDelta(amount)}</span>;

const ComponentRow: React.FC<{
    component: ComponentDiff;
    onSelect?: (componentKey: string) => void;
    selected: boolean;
}> = ({ component, onSelect, selected }) => {
    const header = (
        <>
            <span className="plan-diff-component-name">{component.componentName}</span>
            <span className={`plan-diff-kind plan-diff-kind--${component.kind}`}>{KIND_LABELS[component.kind]}</span>
            <DeltaBadge amount={component.costDelta} />
        </>
    );

    return (
        <li className={`plan-diff-component ${selected ? 'selected' : ''}`}>
            {onSelect ? (
                <button type="button" className="plan-diff-component-header" onClick={() => onSelect(component.componentKey)}>
                    {header}
                </button>
            ) : (
                <div className="plan-diff-component-header">{header}</div>
            )}
            <ul className="plan-diff-items">
                {component.items.map(item => (
                    <li key={item.key} className="plan-diff-item">
                        <div className="plan-diff-item-title">
                            <span className="plan-diff-item-label">{item.label}</span>
                            {item.kind !== 'changed' && (
                                <span className={`plan-diff-kind plan-diff-kind--${item.kind}`}>{KIND_LABELS[item.kind]}</span>
                            )}
                            <DeltaBadge amount={item.costDelta} />
                        </div>
                        {item.description && (
                            <div className={`plan-diff-description ${item.kind === 'removed' ? 'plan-diff-old' : ''}`}>{item.description}</div>
                        )}
                        {item.changes.map(change => (
                            <div key={change.field} className="plan-diff-change">
                                <span className="plan-diff-change-label">{change.label}</span>
                                <span className="plan-diff-old">{change.from}</span>
                                <ArrowRight size={12} className="plan-diff-arrow" aria-label="till" />
                                <span className="plan-diff-new">{change.to}</span>
                            </div>
                        ))}
                    </li>
                ))}
            </ul>
        </li>
    );
};

/** "Ändringar mot aktiv plan": what activating the draft would change, grouped like the component list. */
export const PlanDiffView: React.FC<PlanDiffViewProps> = ({
    diff,
    yearsToShow,
    onYearsToShowChange,
    onSelectComponent,
    selectedComponentKey
}) => {
    const totalDelta = diff.draftTotal - diff.activeTotal;

    return (
        <div className="plan-diff">
            <div className="plan-diff-summary">
                <select
                    className="form-select form-select-sm filter-select"
                    value={yearsToShow}
                    onChange={(e) => onYearsToShowChange(parseInt(e.target.value, 10))}
                    aria-label="Antal år"
                >
                    {[10, 20, 30, 40, 50].map(years => (
                        <option key={years} value={years}>{years} år</option>
                    ))}
                </select>
                <div className="plan-diff-totals">
                    <span className="plan-diff-total-label">Totalt {yearsToShow} år</span>
                    <span>
                        <span className="plan-diff-total-label">Aktiv </span>{formatKr(diff.activeTotal)}
                        <ArrowRight size={14} className="plan-diff-arrow" aria-label="till" />
                        <span className="plan-diff-total-label">Utkast </span>
                        <span className="plan-diff-total-new">{formatKr(diff.draftTotal)}</span>
                        <DeltaBadge amount={totalDelta} />
                    </span>
                </div>
            </div>
            <div className="plan-diff-count">
                {diff.changeCount === 0
                    ? `Inga ändringar mot aktiv plan inom ${yearsToShow} år.`
                    : `${diff.changeCount} ${diff.changeCount === 1 ? 'ändring' : 'ändringar'} mot aktiv plan inom ${yearsToShow} år. Kostnader i dagens priser.`}
            </div>

            {diff.groups.map(group => (
                <section key={group.key} className="plan-diff-group">
                    <h6 className="plan-diff-group-label">{group.label}</h6>
                    <ul className="plan-diff-components">
                        {group.components.map(component => (
                            <ComponentRow
                                key={component.componentKey}
                                component={component}
                                onSelect={onSelectComponent}
                                selected={selectedComponentKey === component.componentKey}
                            />
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
};
